/**
 * Labels rendered as a SEPARATE layer above the schema by design.
 * This is an intentional perf optimization — parallel-loading the label
 * layer separately from the schema layer reduces time-to-first-paint
 * on slow connections.
 *
 * Consequence: route highlight integration deliberately skips labels.
 * Station name texts will remain at their default dim state during route
 * selection. iOS handles label highlighting via Metal draw-order and
 * Android via GLES text-<line>-<code> id derivation — those are
 * platform-specific compensations and are NOT being mirrored on web.
 *
 * Audit ref: docs/superpowers/specs/TASK-273-holistic-audit.md — H-1
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import type { Theme } from '../geometry/types'
import {
  createLabelRasterScheduler,
  type LabelRasterInput,
  type LabelRasterPlan,
  type LabelRasterScheduler,
} from './labelRasterScheduler'
import { rasterizeSvgWindow } from './svgWindowRasterizer'
import { fetchMapAsset } from '../mapDelivery/defaultMapBundleSession'
import { useMapAssetUrl } from '../mapDelivery/useMapAssetUrl'

/**
 * Imperative handle exposed to {@link MetroMapCanvas} so the overlay's CSS
 * transform can be driven from the same rAF tick that redraws the WebGL
 * canvas — without triggering a React re-render on every pan/zoom frame.
 * `viewportPxW/H` (physical device pixels, = canvas.width/height) feed the
 * Stage-3W raster planner.
 */
export interface StationLabelsOverlayHandle {
  apply(
    zoom: number,
    offsetX: number,
    offsetY: number,
    dpr: number,
    viewportPxW: number,
    viewportPxH: number,
  ): void
}

interface StationLabelsOverlayProps {
  readonly theme: Theme
  /**
   * TASK-412 M / P1: when `true` the overlay loads the NIGHT-filtered label
   * variant (`/map-v2/texts-{theme}-night.svg`) — only night-service station +
   * line-name labels — so non-night labels disappear along with the
   * night-filtered map geometry. Night is always dark, so this is
   * effectively `/map-v2/texts-dark-night.svg`. Default `false` = the full label
   * set. Shipped in the map set by the metro-server city build; in a city with
   * a late-night frame (New York, TASK-1136) it is that frame's labels, drawn
   * where the night map has them.
   */
  readonly nightMode?: boolean
  readonly onReady?: () => void
}

/**
 * Station labels overlay — SVG decoded off-thread, DISPLAYED as a cached
 * raster (Stage 3W).
 *
 * The `texts-*.svg` (~7000 paths, 7.5 MB) loads through a hidden `<img>`
 * (browser prefetch + the visual-test settle hook), and its TEXT is then
 * fetched from the same, already-warm HTTP cache entry. What the user
 * SEES is a `<canvas>` holding a rasterization of a viewBox WINDOW of
 * that document, produced by `svgWindowRasterizer` — a source-rect
 * `drawImage` cannot window a dimensionless SVG (see that module's doc):
 *
 *   - pan → translate-only CSS transform (compositor, unchanged);
 *   - zoom gesture → the cached raster is compositor-SCALED only — the
 *     browser no longer re-rasterizes the SVG document every frame (the
 *     pre-existing zoom stutter). Transient blur during the gesture is
 *     accepted;
 *   - gesture end → `labelRasterScheduler` re-rasters ONCE at the settled
 *     zoom (trailing debounce coalesces wheel bursts); the raster is
 *     async (SVG re-parse on the decode thread) and a newer request
 *     supersedes an in-flight one via a generation counter;
 *   - extreme zoom → the raster covers a viewport window instead of the
 *     whole 4096-canvas (pixel-budget guard); a pan escaping the window
 *     re-rasters after the same debounce.
 *
 * `data-labels-ready` flips true only after the FIRST raster lands, so
 * the visual-test gate means "labels are actually painted".
 */
export const StationLabelsOverlay = forwardRef<
  StationLabelsOverlayHandle,
  StationLabelsOverlayProps
>(function StationLabelsOverlay({ theme, nightMode = false, onReady }, handleRef) {
  const imgRef = useRef<HTMLImageElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [loaded, setLoaded] = useState(false)
  // Skip the fade-in under prefers-reduced-motion (a11y) — also makes the
  // label layer deterministic for visual tests (run with reducedMotion:
  // 'reduce'): opacity jumps to 1 on load, so `data-labels-ready="true"`
  // means "fully painted", not "mid-fade".
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const onReadyRef = useRef(onReady)
  useEffect(() => {
    onReadyRef.current = onReady
  })

  // rAF-loop mirrors (the handle's apply() cannot read React state).
  const lastApplyRef = useRef<LabelRasterInput | null>(null)
  const svgTextRef = useRef<string | null>(null)
  const rasterRequestedRef = useRef(false)
  // Supersede guards: bump `rasterGenerationRef` to void an in-flight
  // rasterization; bump `sourceGenerationRef` to void an in-flight text
  // fetch (src swap).
  const rasterGenerationRef = useRef(0)
  const sourceGenerationRef = useRef(0)
  // Origin of the currently rastered region — the transform anchors the
  // canvas at this canvas-space point.
  const rasterOriginRef = useRef({ x: 0, y: 0 })

  const positionCanvas = (input: LabelRasterInput): void => {
    const el = canvasRef.current
    if (el === null) return
    const zoomCss = input.zoom / input.dpr
    const origin = rasterOriginRef.current
    const tx = (origin.x - input.offsetX) * zoomCss
    const ty = (origin.y - input.offsetY) * zoomCss
    el.style.transform = `translate(${tx}px, ${ty}px) scale(${zoomCss})`
  }

  const performRaster = (plan: LabelRasterPlan): void => {
    const svgText = svgTextRef.current
    if (svgText === null || canvasRef.current === null) return
    const generation = ++rasterGenerationRef.current
    void rasterizeSvgWindow(svgText, plan)
      .then((image) => {
        if (generation !== rasterGenerationRef.current) return // superseded
        const canvas = canvasRef.current
        if (canvas === null) return
        canvas.width = plan.pixelW
        canvas.height = plan.pixelH
        // CSS box = region size in canvas units; the per-frame transform
        // scales it by zoomCss, so at the settled zoom the backing store
        // maps 1:1 to physical pixels (crisp), and mid-gesture the
        // compositor scales the cached pixels.
        canvas.style.width = `${plan.region.w}px`
        canvas.style.height = `${plan.region.h}px`
        // Fail-safe: a null 2d context (should not happen in any real
        // browser) leaves the layer empty but must never throw.
        const ctx = canvas.getContext('2d')
        if (ctx !== null) {
          ctx.clearRect(0, 0, plan.pixelW, plan.pixelH)
          ctx.drawImage(image, 0, 0)
        }
        rasterOriginRef.current = { x: plan.region.x, y: plan.region.y }
        const lastApply = lastApplyRef.current
        if (lastApply !== null) positionCanvas(lastApply)
        setLoaded(true)
      })
      .catch((err: unknown) => {
        // Fail-safe: a failed raster logs and reveals the (possibly stale
        // or empty) layer rather than keeping the labels hidden forever.
        console.error('StationLabelsOverlay: label raster failed', err)
        setLoaded(true)
      })
  }
  const performRasterRef = useRef(performRaster)
  useEffect(() => {
    performRasterRef.current = performRaster
  })

  // One scheduler per mounted overlay, created lazily from the first
  // event-handler / rAF call site (ref access during render is not
  // allowed). The onRaster closure indirects through performRasterRef so
  // it never goes stale.
  const schedulerRef = useRef<LabelRasterScheduler | null>(null)
  const getScheduler = (): LabelRasterScheduler => {
    if (schedulerRef.current === null) {
      schedulerRef.current = createLabelRasterScheduler({
        onRaster: (plan) => performRasterRef.current(plan),
      })
    }
    return schedulerRef.current
  }
  useEffect(() => {
    return () => {
      schedulerRef.current?.reset()
    }
  }, [])

  useImperativeHandle(
    handleRef,
    () => ({
      apply(
        zoom: number,
        offsetX: number,
        offsetY: number,
        dpr: number,
        viewportPxW: number,
        viewportPxH: number,
      ): void {
        if (zoom === 0) return
        const input: LabelRasterInput = { zoom, offsetX, offsetY, dpr, viewportPxW, viewportPxH }
        lastApplyRef.current = input
        if (svgTextRef.current !== null) {
          if (!rasterRequestedRef.current) {
            // First frame after the SVG text arrived — raster immediately.
            rasterRequestedRef.current = true
            getScheduler().rasterNow(input)
          } else {
            getScheduler().observe(input)
          }
        }
        positionCanvas(input)
      },
    }),
    [],
  )

  const onLoad = (): void => {
    // onReady drives the host's invalidate() → the next rAF apply()
    // performs the first raster (which then flips `loaded`).
    onReadyRef.current?.()
    const generation = sourceGenerationRef.current
    if (src === null) return
    // The text comes through `fetchMapAsset`, not `fetch(src)`: a downloaded
    // label set is a `blob:` URL, and the CSP's `connect-src` blocks fetching it.
    void fetchMapAsset(labelFile)
      .then((response) => response.text())
      .then((text) => {
        if (generation !== sourceGenerationRef.current) return // src swapped
        svgTextRef.current = text
        const lastApply = lastApplyRef.current
        if (lastApply !== null && !rasterRequestedRef.current) {
          rasterRequestedRef.current = true
          getScheduler().rasterNow(lastApply)
        }
      })
      .catch((err: unknown) => {
        // Fail-safe: the hidden <img> decoded but the text fetch failed —
        // log and reveal the (empty) layer rather than hanging hidden.
        console.error('StationLabelsOverlay: label source fetch failed', err)
        setLoaded(true)
      })
  }

  // The <img> only warms the cache — the raster is drawn from the text. A
  // source the browser will not decode (an untyped `blob:` SVG once did that
  // to every downloaded bundle) must not keep the layer hidden for good.
  const onError = (): void => {
    console.warn('StationLabelsOverlay: label image failed to decode', src)
    onLoad()
  }

  // M/P1: night mode swaps to the pre-filtered night label set. Night is
  // always dark so this resolves to `/texts-dark-night.svg`.
  // TASK-1123: the label SVG of the map bundle this page uses (bundled or
  // downloaded); `null` until resolved, so labels never come from another bundle.
  const labelFile = nightMode ? `texts-${theme}-night.svg` : `texts-${theme}.svg`
  const src = useMapAssetUrl(labelFile)

  // Reset the raster + fade/ready latch whenever the source changes (theme
  // flip OR night toggle) so the new document re-fetches, re-rasters,
  // fades in and re-signals `onReady`. The <img key={src}> remounts, so
  // its onLoad re-fires for the new source; this re-hides the wrapper
  // until then. House pattern: a latch that must clear on a prop change
  // lives in an effect (cf. BubbleOverlay / DesktopStationActionsPopup).
  useEffect(() => {
    sourceGenerationRef.current += 1
    rasterGenerationRef.current += 1
    svgTextRef.current = null
    rasterRequestedRef.current = false
    schedulerRef.current?.reset()
    setLoaded(false)
  }, [src])

  return (
    <div
      aria-hidden
      data-labels-ready={loaded ? 'true' : 'false'}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 1,
        opacity: loaded ? 1 : 0,
        transition: prefersReducedMotion ? 'none' : 'opacity 0.15s ease-out',
      }}
    >
      {/* Hidden loader — warms the HTTP cache + browser decode and keeps
          the visual-test settle hook (waitForMap queries this img). The
          raster pipeline consumes the TEXT fetched in onLoad. `key`
          forces a remount on the src change so onLoad re-fires. */}
      {src !== null && (
        <img
          ref={imgRef}
          key={src}
          src={src}
          alt=""
          onLoad={onLoad}
          onError={onError}
          style={{ display: 'none' }}
        />
      )}
      <canvas
        ref={canvasRef}
        data-labels-raster=""
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          transformOrigin: '0 0',
          willChange: 'transform',
          pointerEvents: 'none',
          imageRendering: 'auto',
        }}
      />
    </div>
  )
})
